/**
 * Quiz Mystery Gift-With-Purchase (QMYSTERYGWP26)
 * -----------------------------------------------
 * Quiz takers who give their email receive the code QMYSTERYGWP26 — a code
 * discount owned by the app function `quiz-gift-discount`: with an item from
 * the "All Products Subscription" collection the code applies (0% on that
 * item, no money off) and makes 1 Vegan Collagen Builder™ (one-time) free
 * once that line is in the cart. Shopify never adds the gift line for a code, so this script
 * closes the gap on the storefront:
 *   - When the cart carries the code (e.g. the shopper arrived through a
 *     /discount/QMYSTERYGWP26 link), it adds 1 × Vegan Collagen Builder™ one-time, tagged
 *     with the line property `_pp_mystery_gwp`.
 *   - It keeps that gift ONLY while Shopify reports the code as applicable
 *     AND the gift line is actually discounted — otherwise it removes it, so
 *     the gift can never sit in the cart at full price (e.g. no qualifying
 *     item, or the discount is paused/expired in Admin).
 *   - Removing the code removes the gift; if the shopper removes the gift
 *     themselves it stays removed for the rest of the browser session.
 *
 * It only ever touches lines it added itself (the `_pp_mystery_gwp`
 * property). The PRICE is enforced by the discount itself, not by this file.
 */
(function () {
  'use strict';

  var CONFIG = {
    code: 'QMYSTERYGWP26',
    giftVariantId: 51580510699808, // Vegan Collagen Builder™ | Powder — SKU 6132
    lineProperty: '_pp_mystery_gwp',
    addedKey: 'pp_mystery_gwp_added', // sessionStorage: other-lines signature while our gift was in the cart
    dismissedKey: 'pp_mystery_gwp_dismissed', // sessionStorage: shopper removed it
    rejectedKey: 'pp_mystery_gwp_rejected' // sessionStorage: cart signature where the code did not apply
  };

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var busy = false;

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function storageRemove(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  function findCode(cart) {
    var codes = cart.discount_codes || [];
    for (var i = 0; i < codes.length; i++) {
      if (String(codes[i].code || '').toUpperCase() === CONFIG.code) return codes[i];
    }
    return null;
  }

  function isOurGiftLine(item) {
    return !!(item.properties && item.properties[CONFIG.lineProperty]);
  }

  // A Vegan Collagen Builder™ one-time line the shopper added themselves — the discount
  // makes that one free already, so no second unit is needed.
  function isOwnGiftVariantLine(item) {
    return item.variant_id === CONFIG.giftVariantId && !item.selling_plan_allocation && !isOurGiftLine(item);
  }

  // Identifies "the rest of the cart" so a rejected attempt is not retried
  // until the shopper actually changes something.
  function otherLinesSignature(cart) {
    return (cart.items || [])
      .filter(function (item) { return !isOurGiftLine(item); })
      .map(function (item) {
        var plan = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : '';
        return item.variant_id + ':' + plan + ':' + item.quantity;
      })
      .sort()
      .join('|');
  }

  function cartRequest(path, payload) {
    return fetch(root + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error('mystery-gwp: ' + path + ' failed (' + response.status + ')');
      return response.json();
    });
  }

  function fetchCart() {
    return fetch(root + 'cart.js', { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    });
  }

  function refreshThemeCart() {
    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  // Sets every line we added to `quantity` (0 removes it).
  function setGiftQuantity(cart, quantity) {
    var updates = {};
    (cart.items || []).forEach(function (item) {
      if (isOurGiftLine(item)) updates[item.key] = quantity;
    });
    return cartRequest('cart/update.js', { updates: updates });
  }

  function giftIsFree(line) {
    return line.final_line_price < line.original_line_price;
  }

  function run(task) {
    busy = true;
    return task()
      .then(function () {
        busy = false;
        refreshThemeCart();
      })
      .catch(function (error) {
        busy = false;
        console.warn(error);
      });
  }

  function evaluate(cart) {
    if (busy || !cart || !cart.items) return;

    var code = findCode(cart);
    var giftLines = cart.items.filter(isOurGiftLine);
    var giftLine = giftLines[0];
    var others = cart.items.filter(function (item) { return !isOurGiftLine(item); });

    // Code gone → the gift goes too, and the offer resets.
    if (!code) {
      storageRemove(CONFIG.addedKey);
      storageRemove(CONFIG.dismissedKey);
      storageRemove(CONFIG.rejectedKey);
      if (giftLine) run(function () { return setGiftQuantity(cart, 0); });
      return;
    }

    if (giftLine) {
      // Never keep our gift unless Shopify is actually giving it away.
      if (!code.applicable || !giftIsFree(giftLine) || others.length === 0 || others.some(isOwnGiftVariantLine)) {
        storageRemove(CONFIG.addedKey);
        storageSet(CONFIG.rejectedKey, otherLinesSignature(cart));
        run(function () { return setGiftQuantity(cart, 0); });
        return;
      }
      if (giftLines.length > 1 || giftLine.quantity !== 1) {
        run(function () {
          var updates = {};
          giftLines.forEach(function (item, i) { updates[item.key] = i === 0 ? 1 : 0; });
          return cartRequest('cart/update.js', { updates: updates });
        });
        return;
      }
      storageSet(CONFIG.addedKey, otherLinesSignature(cart));
      return;
    }

    // No gift line from here on.
    var addedWith = storageGet(CONFIG.addedKey);
    if (addedWith) {
      storageRemove(CONFIG.addedKey);
      // Gift gone but the rest of the cart untouched → the shopper removed it.
      // (If the whole cart changed — cleared or rebuilt by another app — re-add.)
      if (addedWith === otherLinesSignature(cart)) {
        storageSet(CONFIG.dismissedKey, '1');
        return;
      }
    }
    if (storageGet(CONFIG.dismissedKey)) return;
    if (others.length === 0 || others.some(isOwnGiftVariantLine)) return;
    if (storageGet(CONFIG.rejectedKey) === otherLinesSignature(cart)) return;

    run(function () {
      var properties = {};
      properties[CONFIG.lineProperty] = CONFIG.code;
      return cartRequest('cart/add.js', {
        items: [{ id: CONFIG.giftVariantId, quantity: 1, properties: properties }]
      })
        .then(fetchCart)
        .then(function (updated) {
          var updatedCode = findCode(updated);
          var added = (updated.items || []).filter(isOurGiftLine)[0];
          if (added && updatedCode && updatedCode.applicable && giftIsFree(added)) {
            storageSet(CONFIG.addedKey, otherLinesSignature(updated));
            return null;
          }
          // The code does not cover this cart (no qualifying item, or the
          // discount is not active) — take the gift back out.
          storageSet(CONFIG.rejectedKey, otherLinesSignature(updated));
          return setGiftQuantity(updated, 0);
        });
    });
  }

  var refreshTimer = null;
  function evaluateFromServer() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () {
      fetchCart().then(evaluate).catch(function () { /* offline */ });
    }, 400);
  }

  function init() {
    // Impact dispatches cart:change (detail.cart = /cart.js JSON) after its own
    // mutations, and cart:refresh when third parties (Rebuy etc.) change the cart.
    document.addEventListener('cart:change', function (event) {
      var cart = event.detail && event.detail.cart;
      // Only trust payloads that carry discount_codes (a missing key would
      // look like "code removed").
      if (cart && cart.items && cart.discount_codes) evaluate(cart);
      else evaluateFromServer();
    });
    document.addEventListener('cart:refresh', evaluateFromServer);
    evaluateFromServer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
